export const accountStrings = {
  defaults: {
    displayName: 'Hobtalk user',
  },
  profile: {
    eyebrow: 'Account',
    editProfile: 'Edit profile',
    changePhoto: 'Change photo',
    choosePhoto: 'Choose photo',
    takePhoto: 'Take photo',
    updatingPhoto: 'Updating',
    removePhoto: 'Remove',
    removingPhoto: 'Removing...',
    displayNameLabel: 'Display name',
    displayNamePlaceholder: 'Your name',
    saveProfile: 'Save',
    cancel: 'Cancel',
    saveDisplayNameLabel: 'Save display name',
    cancelDisplayNameLabel: 'Cancel display name edit',
    editDisplayNameLabel: 'Edit display name',
    openAccount: 'Open account',
    saving: 'Saving...',
  },
  messages: {
    fileReadFailed: 'Unable to read image file.',
    displayNameRequired: 'Display name is required.',
    displayNameUpdated: 'Display name updated.',
    displayNameSaveFailed: 'Unable to save display name.',
    unsupportedPhotoType: 'Use a JPG, PNG, or WebP image.',
    photoTooLarge: 'Choose an image smaller than 2 MB.',
    photoUpdated: 'Profile photo updated.',
    photoUploadFailed: 'Unable to upload profile photo.',
    photoRemoved: 'Profile photo removed.',
    photoRemoveFailed: 'Unable to remove profile photo.',
  },
  appearance: {
    eyebrow: 'Appearance',
    title: 'Appearance',
    themeTitle: 'Theme',
    themeDetail: 'Choose the visual mode used across Home, hobby modules, and forms.',
    themePreferenceLabel: 'Theme preference',
    system: 'System',
    dark: 'Dark',
    light: 'Light',
  },
  settingChooser: {
    close: 'Close',
  },
  preferences: {
    eyebrow: 'Preferences',
    title: 'Preferences',
    measurementUnits: 'Measurement units',
    measurementUnitsDetail: 'Used by travel, fitness, hiking, and cooking defaults.',
    currency: 'Currency',
    currencyDetail: 'Used for travel planning and future budget features.',
    timeFormat: 'Time format',
    timeFormatDetail: 'How times are displayed across the app.',
    unitOptions: {
      us: 'US',
      metric: 'Metric',
    },
    timeOptions: {
      twelveHour: '12-hour',
      twentyFourHour: '24-hour',
    },
  },
  recommendations: {
    eyebrow: 'Recommendations',
    title: 'Recommendations',
    personalTitle: 'Personal recommendations',
    personalDetail: 'Show hobby ideas and suggestions on Home.',
    personalizationTitle: 'Use my activity to personalize',
    personalizationDetail: 'Use favorites, history, and saved items to tune recommendations.',
  },
  privacy: {
    eyebrow: 'Privacy & sharing',
    title: 'Privacy & sharing',
    defaultPermission: 'Default sharing permission',
    defaultPermissionDetail: 'Used when sharing trips and future shared items.',
    allowEmailSharing: 'Allow people with my email to share with me',
    allowEmailSharingDetail: 'Trips and future shared items can be sent to your Hobtalk profile.',
    sharedAccess: 'Shared access',
    publicSharing: 'Public sharing',
    shareHighlights: 'Share highlights',
    insightRecaps: 'Insight recaps',
    neverShared: 'Private notes, account details, checklist contents, precise routes, and sensitive health data are never included.',
    saveDefaults: 'Save defaults',
    permissionOptions: {
      view: 'View only',
      edit: 'Can edit',
    },
  },
  subscription: {
    eyebrow: 'Subscription',
    title: 'Subscription',
    planEyebrow: 'Current plan',
    freeName: 'Free',
    premiumName: 'Pro',
    hobbyPremiumName: (count) => `Pro for ${count} ${count === 1 ? 'hobby' : 'hobbies'}`,
    bundleName: 'Pro for all hobbies',
    trialName: 'Pro trial',
    freeDetail: 'Explore Hobtalk with a shared free entry allowance.',
    premiumDetail: 'Unlimited entries for the hobbies included in your plan.',
    bundleDetail: 'Unlimited entries across every hobby.',
    hobbyDetail: (names) => `Unlimited access for ${names}.`,
    trialDetail: 'Pro access is active during your trial.',
    currentPlanLabel: 'Current plan',
    includedTitle: 'Pro benefits',
    planLimitsTitle: 'Plan limits',
    premiumBenefitsTitle: 'Pro benefits',
    premiumBenefitsDetail: 'Pro access is active for the hobbies in your plan.',
    price: '$3.99/month per hobby',
    priceDetail: 'Or unlock every hobby with the bundle.',
    plans: {
      monthly: {
        title: 'Monthly',
        price: '$3.99/month per hobby',
        detail: 'Flexible monthly access.',
      },
      annual: {
        title: 'Annual',
        price: '$34.99/year per hobby',
        detail: 'Best value.',
        badge: 'Save',
      },
    },
    renewsOn: 'Renews on',
    trialEndsOn: 'Trial ends on',
    usageTitle: 'Free plan usage',
    usageDetail: 'Free entries are shared across hobbies. Pro hobbies are unlimited.',
    sharedEntries: 'Free entries',
    freeEntryCount: (used, limit) => `${used} of ${limit} free entries used`,
    freeHobbiesTitle: 'Free hobbies',
    premiumHobbiesTitle: 'Pro hobbies',
    allUnlockedTitle: 'All hobbies unlocked',
    usageUnlimited: 'Unlimited',
    usageRemaining: 'remaining',
    usageUsed: 'used',
    limitLabel: 'Details',
    upgrade: 'Upgrade to Pro',
    manageUpgrade: 'Manage plan',
    cancel: 'Cancel plan',
    resume: 'Resume plan',
    betaNote: 'Pro billing is not live yet.',
    loading: 'Loading subscription...',
    loadFailed: 'Unable to load plan details.',
    actionFailed: 'Unable to update plan.',
    actions: {
      trialing: 'Trial active',
      active: 'Pro active',
      canceled: 'Cancels at period end',
      free: 'Free plan',
    },
    benefits: [
      'Unlimited entries for selected hobbies',
      'Personal recommendations based on what you save and revisit',
      'Expanded insights and recaps',
      'Early access to new features',
    ],
    upsell: {
      title: 'Unlock Pro',
      manageTitle: 'Manage plan',
      currentPlan: 'Current plan',
      renews: 'Renews',
      scheduledChange: 'Scheduled change',
      changesOn: (date) => `Changes on ${date}`,
      billingControls: 'Billing controls',
      billingControlsDetail: 'Manage renewal and cancellation.',
      cancellationScheduled: 'Cancellation is scheduled for the end of the current billing period.',
      cancelPlan: 'Cancel plan',
      resumePlan: 'Resume plan',
      cancelConfirm: 'Cancel Pro at the end of the current billing period?',
      manageSubtitle: 'Add hobbies, change billing, or switch to the all-hobbies bundle.',
      limitTitle: (hobby) => `Unlock ${hobby}`,
      subtitle: 'Choose one hobby or unlock every hobby with the bundle.',
      limitSubtitle: () => 'You have used your free entries. Upgrade a hobby or choose the bundle.',
      chooseHobbies: 'Choose hobbies',
      includedHobbies: 'Includes all 10 hobbies: Cooking, Movies, TV Shows, Reading, Traveling, Fitness, Hiking, Music Moments, Dining, and Creative Projects.',
      hobbyPlanTitle: 'Selected hobbies',
      bundlePlanTitle: 'All hobbies',
      popular: 'Popular',
      monthly: 'Monthly',
      annual: 'Annual',
      savePercent: (percent) => `Save ${percent}%`,
      annualValue: (monthlyPrice, annualSavings) => `${monthlyPrice}/month · Save ${annualSavings}/year`,
      hobbyUnitPrice: '$3.99 each / month',
      hobbyUnitAnnualPrice: '$34.99 each / year',
      selectedPrice: (price) => price,
      bundleBetterValue: 'The bundle is the better value for three or more hobbies.',
      bundleApplied: 'All hobbies costs less than this selection, so the bundle will be applied.',
      primaryAction: 'Continue',
      saveChanges: 'Save plan changes',
      pendingChange: 'A plan change is scheduled for your next renewal.',
      keepCurrentPlan: 'Keep current plan',
    },
    hobbyNames: {
      cooking: 'Cooking',
      movies: 'Movies',
      tvshows: 'TV shows',
      reading: 'Reading',
      traveling: 'Traveling',
      hiking: 'Hiking',
      fitness: 'Fitness',
      music: 'Music Moments',
      creative: 'Creative Projects',
      dining: 'Dining',
    },
  },
  logout: {
    title: 'Sign out',
    detail: 'End this browser session and return to the sign-in screen.',
    action: 'Sign out',
  },
};
